import { useState, useEffect } from 'react';
import { collection, query, getDocs, where } from 'firebase/firestore';
import { db } from '../../firebase/config';
import * as Icons from 'lucide-react';
import toast from 'react-hot-toast';

export default function EmailSender() {
  const [loading, setLoading] = useState(false);
  const [recipients, setRecipients] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [progress, setProgress] = useState(0);
  const [currentStatus, setCurrentStatus] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [sendResults, setSendResults] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [groups, setGroups] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState(null);

  // Template-ek betöltése
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const templatesRef = collection(db, 'emailTemplates');
        const querySnapshot = await getDocs(templatesRef);
        const templatesData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setTemplates(templatesData);
      } catch (error) {
        console.error('Error fetching templates:', error);
        toast.error('Hiba történt a sablonok betöltésekor');
      }
    };

    fetchTemplates();
  }, []);

  // Csoportok betöltése
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const groupsRef = collection(db, 'userGroups');
        const querySnapshot = await getDocs(groupsRef);
        const groupsData = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setGroups(groupsData);
      } catch (error) {
        console.error('Error fetching groups:', error);
        toast.error('Hiba történt a csoportok betöltésekor');
      }
    };

    fetchGroups();
  }, []);

  // Felhasználó keresés
  const searchUsers = async (term) => {
    if (term.length < 3) {
      setSearchResults([]);
      return;
    }

    try {
      const usersRef = collection(db, 'users');
      const q = query(
        usersRef,
        where('email', '>=', term),
        where('email', '<=', term + '\uf8ff')
      );
      const snapshot = await getDocs(q);
      const users = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setSearchResults(users);
    } catch (error) {
      console.error('Error searching users:', error);
    }
  };

  // Felhasználó kiválasztása
  const handleUserSelect = (user) => {
    if (!selectedUsers.find(u => u.id === user.id)) {
      setSelectedUsers([...selectedUsers, user]);
      setRecipients(prev => {
        const emails = prev.split(',').map(e => e.trim()).filter(e => e);
        emails.push(user.email);
        return emails.join(', ');
      });
    }
    setSearchTerm('');
    setSearchResults([]);
  };

  // Felhasználó eltávolítása
  const handleRemoveUser = (userId) => {
    const user = selectedUsers.find(u => u.id === userId);
    setSelectedUsers(selectedUsers.filter(u => u.id !== userId));
    setRecipients(prev => {
      const emails = prev.split(',').map(e => e.trim()).filter(e => e !== user.email);
      return emails.join(', ');
    });
  };

  const handleTemplateSelect = (templateId) => {
    const selectedTemplate = templates.find(t => t.id === templateId);
    if (selectedTemplate) {
      setSelectedTemplate(selectedTemplate.id);
      setSubject(selectedTemplate.subject);
      setMessage(selectedTemplate.content);
    }
  };

  // Csoport kiválasztása
  const handleGroupSelect = async (groupId) => {
    const group = groups.find(g => g.id === groupId);
    if (!group) return;

    setSelectedGroup(group);
    
    try {
      // Automatikus szűrők alapján felhasználók lekérése
      const usersRef = collection(db, 'customers');
      let q = query(usersRef);

      if (group.filters.status && group.filters.status !== 'all') {
        q = query(q, where('status', '==', group.filters.status));
      }

      if (group.filters.registrationDateFrom) {
        q = query(q, where('createdAt', '>=', new Date(group.filters.registrationDateFrom)));
      }

      if (group.filters.city) {
        q = query(q, where('address.city', '==', group.filters.city));
      }

      const snapshot = await getDocs(q);
      const filteredUsers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Manuálisan hozzáadott felhasználók hozzáadása
      const allUsers = [...filteredUsers, ...group.users];
      
      // Duplikációk eltávolítása
      const uniqueUsers = Array.from(new Set(allUsers.map(u => u.id)))
        .map(id => allUsers.find(u => u.id === id));

      setSelectedUsers(uniqueUsers);
      setRecipients(uniqueUsers.map(u => u.primaryContact?.email).filter(Boolean).join(', '));
    } catch (error) {
      console.error('Error fetching group users:', error);
      toast.error('Hiba történt a csoport felhasználóinak betöltésekor');
    }
  };

  const handleSend = async () => {
    const emailList = recipients.split(',').map(email => email.trim()).filter(Boolean);
    
    if (!emailList.length || !subject || !message) {
      toast.error('Kérjük, töltse ki az összes mezőt');
      return;
    }

    setLoading(true);
    setIsSending(true);
    setSendResults([]);
    setProgress(0);
    
    try {
      const total = emailList.length;
      
      for (let i = 0; i < emailList.length; i++) {
        const to = emailList[i];
        setCurrentStatus(`Email küldése: ${to} (${i + 1}/${total})`);
        
        try {
          const response = await fetch('https://us-central1-elonyklub.cloudfunctions.net/sendEmail', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              to,
              subject,
              html: message,
              text: message.replace(/<[^>]*>/g, '')
            })
          });

          if (!response.ok) {
            throw new Error(`Hiba a küldés során: ${response.statusText}`);
          }

          setSendResults(prev => [...prev, { email: to, success: true }]);
        } catch (error) {
          setSendResults(prev => [...prev, { email: to, success: false, error: error.message }]);
          console.error(`Hiba a küldés során (${to}):`, error);
        }

        // Progress update
        setProgress(((i + 1) / total) * 100);
        
        // Késleltetés a Gmail rate limit miatt
        if (i < total - 1) {
          await new Promise(resolve => setTimeout(resolve, 1000));
        }
      }

      const successCount = sendResults.filter(r => r.success).length;
      const failCount = sendResults.filter(r => !r.success).length;

      if (failCount === 0) {
        toast.success(`${successCount} email sikeresen elküldve!`);
      } else {
        toast.warning(`${successCount} sikeres és ${failCount} sikertelen küldés`);
      }

      // Form tisztítása
      setRecipients('');
      setSubject('');
      setMessage('');
      setSelectedTemplate('');
      setSelectedUsers([]);
    } catch (error) {
      console.error('Email küldési hiba:', error);
      toast.error('Hiba történt az email küldése során');
    } finally {
      setLoading(false);
      setIsSending(false);
      setCurrentStatus('');
      setProgress(0);
    }
  };

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Email Küldés</h1>
        <p className="mt-1 text-sm text-gray-500">Küldjön emailt a felhasználóknak</p>
        
        {/* Gmail figyelmeztetés */}
        <div className="mt-4 p-4 bg-yellow-50 rounded-lg border border-yellow-200">
          <div className="flex">
            <Icons.AlertTriangle className="h-5 w-5 text-yellow-400 mr-2" />
            <div>
              <h3 className="text-sm font-medium text-yellow-800">
                Email küldési korlátozások
              </h3>
              <div className="mt-1 text-sm text-yellow-700">
                <p>A rendszer Google Workspace-t használ az emailek küldéséhez, ezért kérjük vegye figyelembe:</p>
                <ul className="list-disc list-inside mt-2 space-y-1">
                  <li>Napi küldési limit: 2000 email/nap</li>
                  <li>Percenkénti limit: 60 email/perc</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow p-6 space-y-8">
        {/* Címzettek kiválasztása */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Címzettek kiválasztása</h2>
          
          {/* Felhasználói csoportok */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              1. Felhasználói csoport választása (opcionális)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => handleGroupSelect(group.id)}
                  className={`p-4 border rounded-lg text-left hover:bg-gray-50 ${
                    selectedGroup?.id === group.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                  }`}
                >
                  <div className="font-medium">{group.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{group.description}</div>
                  <div className="mt-2 text-xs text-gray-400">
                    Szűrők: {group.filters.status}, {group.filters.city || 'nincs városszűrő'}
                  </div>
                  {group.users?.length > 0 && (
                    <div className="mt-1 text-xs text-indigo-600">
                      +{group.users.length} manuálisan hozzáadott felhasználó
                    </div>
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Egyéni címzettek */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              2. További címzettek hozzáadása (opcionális)
            </label>
            <div className="relative">
              <input
                type="text"
                value={recipients}
                onChange={(e) => setRecipients(e.target.value)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                placeholder="pelda@email.com, masik@email.com"
              />
            </div>
          </div>
        </div>

        {/* Email tartalom */}
        <div>
          <h2 className="text-lg font-medium text-gray-900 mb-4">Email tartalom</h2>
          
          {/* Email sablonok */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              1. Sablon választása (opcionális)
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {templates.map((template) => (
                <button
                  key={template.id}
                  onClick={() => handleTemplateSelect(template.id)}
                  className={`p-4 border rounded-lg text-left hover:bg-gray-50 ${
                    selectedTemplate === template.id ? 'border-indigo-500 bg-indigo-50' : 'border-gray-200'
                  }`}
                >
                  <div className="font-medium">{template.name}</div>
                  <div className="text-sm text-gray-500 mt-1">{template.subject}</div>
                  <div className="mt-2 text-xs text-gray-400">{template.category}</div>
                </button>
              ))}
            </div>
          </div>

          {/* Email szerkesztő */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              2. Email szerkesztése
            </label>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Tárgy
                </label>
                <input
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Üzenet (HTML)
                </label>
                <textarea
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={10}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-indigo-500 focus:border-indigo-500 font-mono"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Küldés gomb és progress */}
        <div className="mt-6">
          {isSending && (
            <div className="mt-4 space-y-3">
              <div className="text-sm text-gray-600">{currentStatus}</div>
              <div className="w-full bg-gray-200 rounded-full h-2.5">
                <div 
                  className="bg-indigo-600 h-2.5 rounded-full transition-all duration-300"
                  style={{ width: `${progress}%` }}
                />
              </div>
            </div>
          )}

          {sendResults.length > 0 && (
            <div className="mt-4">
              <h3 className="text-sm font-medium text-gray-700 mb-2">Küldési eredmények</h3>
              <div className="space-y-2 max-h-40 overflow-auto">
                {sendResults.map((result, index) => (
                  <div 
                    key={index}
                    className={`flex items-center justify-between p-2 rounded-lg ${
                      result.success ? 'bg-green-50' : 'bg-red-50'
                    }`}
                  >
                    <div className="flex items-center">
                      {result.success ? (
                        <Icons.CheckCircle className="h-5 w-5 text-green-500 mr-2" />
                      ) : (
                        <Icons.XCircle className="h-5 w-5 text-red-500 mr-2" />
                      )}
                      <span className="text-sm">{result.email}</span>
                    </div>
                    {!result.success && (
                      <span className="text-xs text-red-600">{result.error}</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          <div className="flex justify-end mt-4">
            <button
              onClick={handleSend}
              disabled={loading}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center"
            >
              {loading ? (
                <>
                  <Icons.Loader2 className="animate-spin h-5 w-5 mr-2" />
                  Küldés folyamatban...
                </>
              ) : (
                <>
                  <Icons.Send className="h-5 w-5 mr-2" />
                  Email Küldése
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
} 